import { debounce } from 'lodash-es';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useRefs } from './useRefs';

interface Translate {
  x: number;
  y: number;
}

interface Direction {
  isTop: boolean;
  isBottom: boolean;
  isLeft: boolean;
  isRight: boolean;
}

interface UseDragMoveProps {
  disabled?: boolean;
  initTranslate?: Translate;
  thresholdLeft?: number;
  thresholdRight?: number;
  thresholdTop?: number;
  thresholdBottom?: number;
  showLabelLeft?: boolean;
  showLabelRight?: boolean;
  showLabelTop?: boolean;
  showLabelBottom?: boolean;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
}

interface UseDragResult {
  dragRef: React.RefObject<HTMLDivElement | null>;
  hoverOverRef: React.RefObject<HTMLDivElement | null>;
  hoverLeaveRef: React.RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  direction: Direction;
  isMouseOver: boolean;
  style: React.CSSProperties;
}
export const useDragMove = ({
  disabled = false,
  initTranslate = { x: 0, y: 0 },
  thresholdLeft = 10,
  thresholdRight = 10,
  thresholdTop = 10,
  thresholdBottom = 10,
  showLabelLeft = true,
  showLabelRight = true,
  showLabelTop = false,
  showLabelBottom = false,
  marginLeft = 0,
  marginRight = 0,
  marginTop = 0,
  marginBottom = 0,
}: UseDragMoveProps): UseDragResult => {
  const [translate, setTranslate] = useState<Translate>(initTranslate);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [direction, setDirection] = useState<Direction>({
    isTop: false,
    isBottom: false,
    isLeft: false,
    isRight: false,
  });
  const [isMouseOver, setIsMoveOver] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const startPosition = useRef<Translate>({ x: 0, y: 0 });
  const startTranslateRef = useRef(translate);
  const lastTranslateRef = useRef(translate);
  const adsorpTranslateRef = useRef(translate);
  const hoverOverRef = useRef<HTMLDivElement>(null);
  const hoverLeaveRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging) return;
      const deltaX = clientX - startPosition.current.x;
      const deltaY = clientY - startPosition.current.y;
      setTranslate({
        x: startTranslateRef.current.x + deltaX,
        y: startTranslateRef.current.y + deltaY,
      });
    },
    [isDragging]
  );

  const calculateNewPosition = useCallback(
    (
      rect: DOMRect,
      viewportWidth: number,
      viewportHeight: number,
      originalLeft: number,
      originalTop: number
    ): [Translate, Direction] => {
      let newX = lastTranslateRef.current.x;
      let newY = lastTranslateRef.current.y;

      const initTop = dragRef.current!.offsetTop;
      const initRight =
        viewportWidth - dragRef.current!.offsetLeft - dragRef.current!.offsetWidth;

      const newDirection: Direction = {
        isTop: false,
        isBottom: false,
        isLeft: false,
        isRight: false,
      };

      if (rect.left < thresholdLeft) {
        const leftComputedVal = 0 - originalLeft;
        const leftTargetVal = rect.width + initRight - viewportWidth;
        const leftVal = leftComputedVal !== leftTargetVal ? leftTargetVal : leftComputedVal;

        newX = (showLabelLeft ? leftVal - rect.width : leftVal) + marginLeft;
        if (showLabelLeft) {
          newDirection.isLeft = true;
        }
      } else if (rect.right > viewportWidth - thresholdRight) {
        const rightComputedVal = viewportWidth - rect.width - originalLeft;
        const rightTargetVal = initRight;
        const rightVal = rightComputedVal !== rightTargetVal ? rightTargetVal : rightComputedVal;

        newX = (showLabelRight ? rightVal + rect.width : rightVal) - marginRight;
        if (showLabelRight) {
          newDirection.isRight = true;
        }
      }

      if (rect.top < thresholdTop) {
        const topComputedVal = 0 - originalTop;
        const topTargetVal = -initTop;
        const topVal = topComputedVal !== topTargetVal ? topTargetVal : topComputedVal;

        newY = (showLabelTop ? topVal - rect.height : topVal) + marginTop;
        if (showLabelTop) {
          newDirection.isTop = true;
        }
      } else if (rect.bottom > viewportHeight - thresholdBottom) {
        const bottomComputedVal = viewportHeight - rect.height - originalTop;
        const bottomTargetVal = viewportHeight - rect.height - initTop;
        const bottomVal =
          bottomComputedVal !== bottomTargetVal ? bottomTargetVal : bottomComputedVal;

        newY = (showLabelBottom ? bottomVal + rect.height : bottomVal) - marginBottom;
        if (showLabelBottom) {
          newDirection.isBottom = true;
        }
      }

      return [{ x: newX, y: newY }, newDirection];
    },
    [
      marginBottom,
      marginLeft,
      marginRight,
      marginTop,
      showLabelBottom,
      showLabelLeft,
      showLabelRight,
      showLabelTop,
      thresholdBottom,
      thresholdLeft,
      thresholdRight,
      thresholdTop,
    ]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);

    const node = dragRef.current;
    if (!node || disabled) return;

    const rect = node.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const originalLeft = rect.left - lastTranslateRef.current.x;
    const originalTop = rect.top - lastTranslateRef.current.y;

    const [newPosition, newDirection] = calculateNewPosition(
      rect,
      viewportWidth,
      viewportHeight,
      originalLeft,
      originalTop
    );
    setTranslate(newPosition);
    setDirection(newDirection);
    setIsMoveOver(Object.values(newDirection).some((v) => v));
    adsorpTranslateRef.current = newPosition;
  }, [calculateNewPosition, disabled]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => handleMove(e.clientX, e.clientY),
    [handleMove]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    },
    [handleMove]
  );

  const onMouseUp = useCallback(() => handleEnd(), [handleEnd]);

  const onTouchEnd = useCallback(() => handleEnd(), [handleEnd]);

  const handleStart = useCallback(
    (clientX: number, clientY: number) => {
      setIsDragging(true);
      setDirection({
        isTop: false,
        isBottom: false,
        isLeft: false,
        isRight: false,
      });
      startPosition.current = { x: clientX, y: clientY };
      startTranslateRef.current = { x: translate.x, y: translate.y };
    },
    [translate.x, translate.y]
  );

  const latest = useRefs({ handleStart });

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      latest.current.handleStart(e.clientX, e.clientY);
    },
    [latest, disabled]
  );

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      latest.current.handleStart(touch.clientX, touch.clientY);
    },
    [latest]
  );

  const onMouseOver = useCallback(() => {
    const dragNode = dragRef.current;
    if (!dragNode) return;

    const rect = dragNode.getBoundingClientRect();
    const newTranslate = { ...adsorpTranslateRef.current };

    if (direction.isLeft) newTranslate.x += rect.width;
    if (direction.isRight) newTranslate.x -= rect.width;
    if (direction.isTop) newTranslate.y += rect.height;
    if (direction.isBottom) newTranslate.y -= rect.height;

    setTranslate(newTranslate);
    setIsMoveOver(false);
  }, [direction]);

  const onMouseLeave = debounce(
    useCallback(() => {
      if (Object.values(direction).every((v) => !v)) return;
      setTranslate(adsorpTranslateRef.current);
      const timer = setTimeout(() => {
        setIsMoveOver(true);
        clearTimeout(timer);
      }, 200);
    }, [direction]),
    200
  );

  useEffect(() => {
    lastTranslateRef.current = translate;
  }, [translate]);

  useEffect(() => {
    setTranslate({ x: 0, y: 0 });
    setIsMoveOver(false);
    setDirection({
      isTop: false,
      isBottom: false,
      isLeft: false,
      isRight: false,
    });
  }, [disabled]);

  useEffect(() => {
    const dragNode = dragRef.current;
    if (!dragNode) return;

    dragNode.addEventListener('touchstart', onTouchStart, { passive: false });
    dragNode.addEventListener('mousedown', onMouseDown);
    return () => {
      dragNode.removeEventListener('touchstart', onTouchStart);
      dragNode.removeEventListener('mousedown', onMouseDown);
    };
  }, [onTouchStart, onMouseDown, dragRef.current?.id]);

  useEffect(() => {
    if (isDragging && !disabled) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, disabled, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  useEffect(() => {
    if (disabled) return;

    const overNode = hoverOverRef.current;

    if (overNode) {
      overNode.addEventListener('mouseover', onMouseOver);
    }

    const leaveNode = hoverLeaveRef.current;
    if (leaveNode) {
      leaveNode.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      overNode?.removeEventListener('mouseover', onMouseOver);
      leaveNode?.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [disabled, onMouseOver, onMouseLeave]);

  useEffect(() => {
    const onResize = () => {
      handleEnd();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [handleEnd]);

  return {
    dragRef,
    hoverLeaveRef,
    hoverOverRef,
    isDragging,
    direction,
    isMouseOver,
    style: {
      transform: `translate(${translate.x}px, ${translate.y}px)`,
      userSelect: 'none',
      touchAction: 'none',
      transition: isDragging || disabled ? 'none' : 'transform 0.2s ease',
      cursor: isDragging ? 'grabbing' : disabled ? 'inherit' : 'grab',
    },
  };
};
